import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/redux/store';
import type { Launch } from '@/redux/slices/focusNewScheduleSlice';
import { createRightsGroup, type GroupedRight } from '@/components/Focus/utils/rightsValidation';

export const selectFocusNewScheduleSlice = (state: RootState) => state.focusNewSchedule;

export const selectFocusNewScheduleView = createSelector(selectFocusNewScheduleSlice, (focusNewSchedule) => focusNewSchedule.view);

export const selectFocusRightsPossibleById = createSelector(
  selectFocusNewScheduleSlice,
  (_: RootState, id: number) => id,
  (focusNewSchedule, id) => focusNewSchedule.rightsPossibleById[id]
);

export const selectFocusRightsPossibleBySchedulingId = createSelector(
  selectFocusNewScheduleSlice,
  (_: RootState, id: number) => id,
  (focusNewSchedule, id) => focusNewSchedule.rightsPossibleBySchedulingId[id]
);

export const selectLaunchList = createSelector(selectFocusNewScheduleSlice, (focusNewSchedule): Launch[] => focusNewSchedule.launchList ?? []);

export const selectLastIncrementalLaunch = createSelector(selectLaunchList, (launchList): Launch | null => {
  const incrementalLaunches = launchList.filter((launch) => launch.type === 'incremental');
  return incrementalLaunches.length ? incrementalLaunches[incrementalLaunches.length - 1] : null;
});

export const selectFocusRightsValidateByVersionId = createSelector(
  selectFocusNewScheduleSlice,
  (_: RootState, versionId: number) => versionId,
  (_: RootState, _versionId: number, launchId?: string) => launchId,
  (focusNewSchedule, versionId, launchId) => {
    const validationByVersion = focusNewSchedule.rightsValidateByVersionId?.[versionId];

    if (!validationByVersion) {
      return null;
    }

    if (launchId) {
      return validationByVersion.launchesById?.[launchId] ?? null;
    }

    return validationByVersion.base ?? null;
  }
);

export const selectFocusGroupedRightsValidateByVersionId = createSelector(
  selectFocusRightsValidateByVersionId,
  (validationResult): GroupedRight[] => {
    if (validationResult === 'valid' || !Array.isArray(validationResult)) {
      return [];
    }

    return createRightsGroup(validationResult);
  }
);
